//Dashboard.js

import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import PageSnackbars from "../common/PageSnackbars";
import { usePageSnackbar } from "../common/usePageSnackbar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, addDays, subDays } from "date-fns";
import WorldMap from "react-svg-worldmap";
import CircleIcon from "@mui/icons-material/Circle";
import { getName } from "country-list";
import countryListData from "country-list/data.json";
import Notifications from "../Notifications";
import DashboardProject from "../DashboardProject";
import DashboardTraffics from "../DashboardTraffics";
import DashboardSMS from "../DashboardSMS";
import WelcomeDialog from "../WelcomeDialog";
import OnboardingProgress from "../OnboardingProgress";
import useOnboarding from "../useOnboarding";
import WarningIcon from "@mui/icons-material/Warning";
import { ROLE_USER, isEndUserRole } from "../../utils/userRoles";
import { PLAN_LIMITS, resolvePlanLimit } from "../../config/billingPlans";
import { FEATURES } from "../../config/features";
import {
  PageShell,
  Card,
  CardContent,
  Tabs,
  Tab,
  StatCard,
  Button,
  emailMetricIcons,
  DashboardMetricIcon,
  Chip,
} from "../ui";
import {
  acme,
  acmeDashboardPanelSx,
  acmeInfoCardSx,
  acmeInfoCardActiveSx,
  acmeActiveBadgeSx,
  acmeNeutralColorScale,
  acmeDashboardTabGapSx,
  acmeDashboardStatGridSx,
  acmeDashboardCardContentSx,
  acmeDashboardPanelCardSx,
  acmeDashboardSectionTitleSx,
  acmeDashboardSectionDescSx,
  acmePageHeaderContentSx,
  DASHBOARD_STAT_GRID_SPACING,
  DASHBOARD_CHART_GRID_SPACING,
} from "../../theme/acmeTheme";

// Создаем прямой доступ к данным о странах из JSON
const countriesMap = {};

// Инициализируем словарь стран из country-list
try {
  console.log("Initializing country map from country-list...");
  // Логируем количество стран в датасете
  console.log("Total entries in country-list data:", countryListData.length);

  let initializedCount = 0;
  // Заполняем словарь стран кодами и именами
  countryListData.forEach((country) => {
    if (country && country.code && country.name) {
      const upperCode = country.code.toUpperCase();
      // Добавляем только если код еще не существует, чтобы избежать дублирования
      if (!countriesMap[upperCode]) {
        countriesMap[upperCode] = country.name;
        initializedCount++;
      }
    } else {
      console.warn("Invalid country data entry in countryListData:", country);
    }
  });

  console.log(
    "Initialized country map with",
    initializedCount,
    "unique countries from data.json. Sample:",
    Object.entries(countriesMap).slice(0, 5),
  );
} catch (error) {
  console.error("Error initializing country map:", error);
}

// Создаем безопасную обертку для функции getName из country-list
const safeGetName = (code) => {
  if (!code || typeof code !== "string") {
    console.warn("Invalid input to safeGetName:", code);
    return null;
  }

  try {
    // Проверяем, что функция существует
    if (typeof getName !== "function") {
      console.warn("getName function not available");
      return null;
    }

    return getName(code);
  } catch (error) {
    console.warn(`Error in safeGetName for code ${code}:`, error);
    return null;
  }
};

const iconMap = emailMetricIcons;

const changeIndicatorSx = (positive) => ({
  position: "absolute",
  top: 12,
  right: 12,
  borderRadius: acme.radius.sm,
  px: 1,
  py: 0.25,
  fontSize: "0.75rem",
  fontWeight: 700,
  backgroundColor: positive ? "#dcfce7" : "#fee2e2",
  color: positive ? "#166534" : "#991b1b",
});

const Dashboard = () => {
  const [value, setValue] = useState(0);

  // Onboarding hook
  const {
    onboardingData,
    showWelcome,
    handleWelcomeClose,
    handleWelcomeStart,
    refreshOnboardingData,
    isOnboardingComplete,
    loading: onboardingLoading,
  } = useOnboarding();
  const [user, setUser] = useState({
    login: "User",
    plan: "Basic",
    emailsUsed: 0,
    emailLimit: PLAN_LIMITS.Basic,
    smsCredits: 0,
    smsCreditsPeak: 0,
    smsCreditsEur: 0,
    allSentSms: 0,
    unlimitedSms: false,
    purchasedDate: "2023-10-01",
    nextPaymentDate: "2023-11-01",
    role: ROLE_USER,
  });

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
  });
  const [openStartDatePicker, setOpenStartDatePicker] = useState(false);
  const [openEndDatePicker, setOpenEndDatePicker] = useState(false);

  // Dashboard statistics for Email tab
  const [emailStats, setEmailStats] = useState({
    campaigns: { count: 0, rate: 0, change: 0, loading: false },
    recipients: { count: 0, rate: 0, change: 0, loading: false },
    opens: { count: 0, rate: 0, change: 0, loading: false },
    clicks: { count: 0, rate: 0, change: 0, loading: false },
    unsubscribed: { count: 0, rate: 0, change: 0, loading: false },
  });

  // Add state for showing all countries
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [countryDataLoading, setCountryDataLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("recipients"); // Default metric to show on the map

  // Country statistics with proper ISO codes for the map
  const [countryData, setCountryData] = useState([]);

  // Device statistics
  const [deviceStats, setDeviceStats] = useState({
    mobile: 0,
    desktop: 0,
  });
  const [deviceDataLoading, setDeviceDataLoading] = useState(false);

  const { setErrorMessage, snackbarProps } = usePageSnackbar();

  const dashboardTabs = useMemo(() => {
    const tabs = ["emails"];
    if (FEATURES.PROJECTS_ENABLED) tabs.push("projects");
    tabs.push("sms");
    tabs.push("notifications");
    if (!isEndUserRole(user.role)) tabs.push("traffics");
    return tabs;
  }, [user.role]);

  const activeTab = dashboardTabs[value] || dashboardTabs[0];

  const getTabName = (tabIndex) => dashboardTabs[tabIndex] || dashboardTabs[0];

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleDateRangeChange = (type, newDate) => {
    // Закрыть открытые пикеры в зависимости от типа
    if (type === "startDate") {
      setOpenStartDatePicker(false);
    } else {
      setOpenEndDatePicker(false);
    }

    // Новый диапазон дат
    let newDateRange;

    // If the selected end date is before the start date, adjust it
    if (type === "startDate" && newDate && newDate > dateRange.endDate) {
      newDateRange = {
        startDate: newDate,
        endDate: addDays(newDate, 1),
      };
    }
    // If the selected start date is after the end date, adjust it
    else if (type === "endDate" && newDate && dateRange.startDate > newDate) {
      newDateRange = {
        startDate: subDays(newDate, 1),
        endDate: newDate,
      };
    }
    // Normal case - just update the selected date
    else {
      newDateRange = {
        ...dateRange,
        [type]: newDate,
      };
    }

    // Устанавливаем новый диапазон дат
    setDateRange(newDateRange);

    if (value === 0) {
      setCountryDataLoading(true);
      setDeviceDataLoading(true);
    }
  };

  // Calculate the percentage from mobile and desktop
  const calculateMobilePercentage = () => {
    const total = deviceStats.mobile + deviceStats.desktop;
    return total > 0 ? Math.round((deviceStats.mobile / total) * 100) : 0;
  };

  // Функция fetchCountryData
  const fetchCountryData = async (metric = "recipients") => {
    try {
      setCountryDataLoading(true);

      // Формируем параметры запроса
      const params = {
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
        tab: getTabName(value),
        metric,
      };

      console.log(`Fetching country data with params:`, params);

      // Запрос к API
      try {
        const response = await axios.get("/api/dashboard/country-stats", {
          params,
        });

        if (response.data && Array.isArray(response.data)) {
          console.log("Raw country data from API:", response.data);

          // Создаем новый массив с правильной структурой для отображения
          const formattedData = [];

          // Обрабатываем данные
          for (const item of response.data) {
            // Проверяем валидность данных
            if (!item || typeof item !== "object" || !item.country) {
              console.warn("Skipping invalid country data item:", item);
              continue;
            }

            // Нормализуем код страны (всегда в верхнем регистре)
            const countryCode = String(item.country).trim().toUpperCase();
            const numValue = parseInt(item.value, 10);
            if (isNaN(numValue)) {
              console.warn(
                `Invalid value for country ${countryCode}:`,
                item.value,
              );
              continue;
            }

            if (countryCode === "XX" || countryCode === "PROXY") {
              formattedData.push({
                country: "XX",
                value: numValue,
                name: item.name || "Unknown",
              });
              continue;
            }

            if (countryCode.length !== 2) {
              console.warn(`Skipping invalid country code: ${countryCode}`);
              continue;
            }

            let countryName = item.name || countriesMap[countryCode];
            if (!countryName) {
              countryName = safeGetName(countryCode);
              if (countryName) {
                countriesMap[countryCode] = countryName;
              }
            }

            formattedData.push({
              country: countryCode,
              value: numValue,
              name:
                item.name ||
                countryName ||
                (countryCode === "XX" ? "Unknown" : countryCode),
            });
          }

          // Сортируем по убыванию значения
          formattedData.sort((a, b) => b.value - a.value);

          console.log(
            `Processed ${formattedData.length} countries. Top 3:`,
            formattedData
              .slice(0, 3)
              .map((c) => `${c.name} (${c.country}): ${c.value}`)
              .join(", "),
          );

          // Обновляем состояние с отформатированными данными
          setCountryData(formattedData);
        } else {
          console.warn("API returned empty or invalid country data");
          setCountryData([]);
        }
      } catch (apiError) {
        console.error("Dashboard API error:", apiError);
        setCountryData([]);
        setErrorMessage(
          "Failed to load country statistics. API error occurred.",
        );
      }
    } catch (error) {
      console.error("Failed to fetch country stats:", error);
      setCountryData([]);
      setErrorMessage("Failed to load country statistics. Please try again.");
    } finally {
      setCountryDataLoading(false);
    }
  };

  // Fetch device statistics based on selected metric
  const fetchDeviceStats = async (metric = "recipients") => {
    try {
      setDeviceDataLoading(true);

      // Формируем параметры запроса
      const params = {
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
        tab: getTabName(value),
        metric,
      };

      // Запрос к API
      try {
        const response = await axios.get("/api/dashboard/device-stats", {
          params,
        });

        if (response.data && typeof response.data === "object") {
          const deviceData = {
            mobile: response.data.mobile || 0,
            desktop: response.data.desktop || 0,
          };

          console.log("Received device data:", deviceData);
          setDeviceStats(deviceData);
        } else {
          console.log("API returned empty device data or incorrect format");
          setDeviceStats({ mobile: 0, desktop: 0 });
        }
      } catch (apiError) {
        console.error("Dashboard device API error:", apiError);
        setDeviceStats({ mobile: 0, desktop: 0 });
        setErrorMessage(
          "Failed to load device statistics. API error occurred.",
        );
      }
    } catch (error) {
      console.error("Failed to fetch device stats:", error);
      setDeviceStats({ mobile: 0, desktop: 0 });
      setErrorMessage("Failed to load device statistics. Please try again.");
    } finally {
      setDeviceDataLoading(false);
    }
  };

  // Fetch user info
  const fetchUserInfo = async () => {
    try {
      // Get user profile information
      const userResponse = await axios.get("/api/users/me");

      // Get billing/plan information
      const planResponse = await axios.get("/api/billing/current-plan");

      let smsCredits = 0;
      let smsCreditsPeak = 0;
      let smsCreditsEur = 0;
      let allSentSms = 0;
      let unlimitedSms = false;

      try {
        const smsResponse = await axios.get("/api/sms-campaigns/credits");
        smsCredits = Math.max(
          0,
          Math.floor(Number(smsResponse.data?.smsCredits) || 0),
        );
        smsCreditsPeak = Math.max(
          smsCredits,
          Math.floor(Number(smsResponse.data?.smsCreditsPeak) || 0),
        );
        smsCreditsEur = Number(smsResponse.data?.smsCreditsEur) || 0;
        allSentSms = Math.max(
          0,
          Math.floor(Number(smsResponse.data?.allSentSms) || 0),
        );
        unlimitedSms = !!smsResponse.data?.unlimitedSms;
      } catch (smsError) {
        console.error("Failed to fetch SMS credits:", smsError);
      }

      // Process dates - используем сохраненную дату покупки из базы данных
      const today = new Date();
      const userCreatedAt = new Date(userResponse.data.createdAt);

      // Используем planPurchasedDate из ответа API или из userResponse, если доступно
      const planPurchasedDate = planResponse.data.planPurchasedDate
        ? new Date(planResponse.data.planPurchasedDate)
        : userResponse.data.planPurchasedDate
          ? new Date(userResponse.data.planPurchasedDate)
          : null;

      // Для подписок используем период подписки
      let purchasedDate, nextPaymentDate;
      if (
        planResponse.data.isSubscription &&
        planResponse.data.subscriptionInfo
      ) {
        purchasedDate = format(
          new Date(planResponse.data.subscriptionInfo.current_period_start),
          "yyyy-MM-dd",
        );
        nextPaymentDate = format(
          new Date(planResponse.data.subscriptionInfo.current_period_end),
          "yyyy-MM-dd",
        );
      } else if (planPurchasedDate) {
        // Используем сохраненную дату покупки плана
        purchasedDate = format(planPurchasedDate, "yyyy-MM-dd");
        // Находим следующий месяц с тем же днем, который еще не наступил
        const dayOfMonth = planPurchasedDate.getDate();
        const nextPayment = new Date(
          today.getFullYear(),
          today.getMonth(),
          dayOfMonth,
        );
        // Если этот день уже прошел в текущем месяце, берем следующий месяц
        if (nextPayment <= today) {
          nextPayment.setMonth(nextPayment.getMonth() + 1);
        }
        nextPaymentDate = format(nextPayment, "yyyy-MM-dd");
      } else {
        // Для Basic плана: используем дату создания аккаунта
        purchasedDate = format(userCreatedAt, "yyyy-MM-dd");
        // Находим следующий месяц с тем же днем, который еще не наступил
        const dayOfMonth = userCreatedAt.getDate();
        const nextPayment = new Date(
          today.getFullYear(),
          today.getMonth(),
          dayOfMonth,
        );
        // Если этот день уже прошел в текущем месяце, берем следующий месяц
        if (nextPayment <= today) {
          nextPayment.setMonth(nextPayment.getMonth() + 1);
        }
        nextPaymentDate = format(nextPayment, "yyyy-MM-dd");
      }

      // For Basic plan - calculate renewal date based on registration date
      const basicPlanRenewalDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        userCreatedAt.getDate(),
      );

      // If today's date has passed the renewal day this month, move to next month
      if (today.getDate() > userCreatedAt.getDate()) {
        basicPlanRenewalDate.setMonth(basicPlanRenewalDate.getMonth() + 1);
      }

      // Combine all information
      const planName = planResponse.data.planName || "Basic";
      setUser({
        ...userResponse.data,
        plan: planName,
        emailsUsed: planResponse.data.usedEmails || 0,
        emailLimit: resolvePlanLimit(planName, planResponse.data.planLimit),
        smsCredits,
        smsCreditsPeak,
        smsCreditsEur,
        allSentSms,
        unlimitedSms,
        purchasedDate,
        nextPaymentDate,
        basicPlanRenewalDate: format(basicPlanRenewalDate, "yyyy-MM-dd"),
        role: userResponse.data.role || ROLE_USER,
      });
    } catch (error) {
      console.error("Failed to fetch user info:", error);
      setErrorMessage(
        "Failed to load user information. Please try refreshing the page.",
      );
    }
  };

  // Fetch dashboard statistics based on date range
  const fetchDashboardStats = async () => {
    try {
      console.log(
        "Fetching stats for date range:",
        format(dateRange.startDate, "yyyy-MM-dd"),
        "to",
        format(dateRange.endDate, "yyyy-MM-dd"),
      );

      setEmailStats((prev) => ({
        campaigns: { ...prev.campaigns, loading: true },
        recipients: { ...prev.recipients, loading: true },
        opens: { ...prev.opens, loading: true },
        clicks: { ...prev.clicks, loading: true },
        unsubscribed: { ...prev.unsubscribed, loading: true },
      }));

      const params = {
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      };
      const { data } = await axios.get("/api/dashboard/email-stats", { params });
      const cur = data.current || {};
      const totalRecipients = cur.recipients || 0;
      const unreachableRecipients = cur.failedDeliveries || 0;
      const totalOpens = cur.opens || 0;
      const totalClicks = cur.clicks || 0;
      const totalUnsubscribed = cur.unsubscribed || 0;

      const calculateRate = (value, total) =>
        total > 0 ? Math.round((value / total) * 100) : 0;

      const deliverySuccessRate =
        totalRecipients > 0
          ? Math.round(
              ((totalRecipients - unreachableRecipients) / totalRecipients) *
                100,
            )
          : 0;

      setEmailStats({
        campaigns: {
          count: cur.campaigns || 0,
          rate: null,
          change: null,
          loading: false,
        },
        recipients: {
          count: totalRecipients,
          rate: deliverySuccessRate,
          change: unreachableRecipients,
          loading: false,
        },
        opens: {
          count: totalOpens,
          rate: calculateRate(totalOpens, totalRecipients),
          change: null,
          loading: false,
        },
        clicks: {
          count: totalClicks,
          rate: calculateRate(totalClicks, totalRecipients),
          change: null,
          loading: false,
        },
        unsubscribed: {
          count: totalUnsubscribed,
          rate: calculateRate(totalUnsubscribed, totalRecipients),
          change: null,
          loading: false,
        },
      });
    } catch (error) {
      console.error("Failed to fetch campaign stats:", error);

      setEmailStats((prev) => ({
        campaigns: { ...prev.campaigns, loading: false },
        recipients: { ...prev.recipients, loading: false },
        opens: { ...prev.opens, loading: false },
        clicks: { ...prev.clicks, loading: false },
        unsubscribed: { ...prev.unsubscribed, loading: false },
      }));
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchUserInfo();
    // Refresh onboarding data after user info is loaded
    setTimeout(() => {
      refreshOnboardingData();
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time bootstrap
  }, []);

  useEffect(() => {
    if (value !== 0) return;

    setCountryDataLoading(true);
    setDeviceDataLoading(true);
    fetchDashboardStats();
    fetchCountryData(selectedMetric);
    fetchDeviceStats(selectedMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- email tab only
  }, [dateRange, value, selectedMetric]);

  // Handle click on a stat card to update the country data
  const handleStatCardClick = (metric) => {
    setSelectedMetric(metric);
  };

  // Render stat card with click handler
  const renderStatBox = (title, iconKey, value, rate, change, loading) => (
    <StatCard
      title={title}
      icon={iconMap[iconKey]}
      value={value}
      rate={iconKey !== "campaigns" ? rate : 0}
      loading={loading}
      selected={selectedMetric === iconKey}
      onClick={iconKey !== "campaigns" ? () => handleStatCardClick(iconKey) : undefined}
    >
      {iconKey === "recipients" &&
        !loading &&
        (change > 0 ? (
          <Tooltip title="Number of unreachable recipients" arrow>
            <Box sx={changeIndicatorSx(false)}>{change}</Box>
          </Tooltip>
        ) : value > 0 && rate === 100 ? (
          <Tooltip title="All recipients successfully received the email" arrow>
            <Box sx={changeIndicatorSx(true)}>
              <CheckCircleIcon sx={{ fontSize: "0.9rem" }} />
            </Box>
          </Tooltip>
        ) : null)}
    </StatCard>
  );

  // Function to get metric friendly name
  const getMetricName = (metric) => {
    const metricNames = {
      campaigns: "Campaigns",
      recipients: "Recipients",
      opens: "Opens",
      clicks: "Clicks",
      unsubscribed: "Unsubscribed",
    };
    return metricNames[metric] || "Recipients";
  };

  // Configure color ranges for the map based on values
  const getColorScale = () => ({
    colorScale: acmeNeutralColorScale,
    noDataColor: acme.colors.muted,
  });

  // Создаем кастомную обертку для WorldMap компонента
  const EnhancedWorldMap = ({ data, ...otherProps }) => {
    // Преобразуем данные и добавляем имена стран
    const enhancedData = useMemo(() => {
      // Проверяем входные данные
      if (!Array.isArray(data) || data.length === 0) {
        console.log("Empty data for EnhancedWorldMap");
        return [];
      }

      // Sort data by value to determine value distribution for coloring
      const sortedData = [...data].sort((a, b) => a.value - b.value);
      const dataLength = sortedData.length;

      // Function to assign color index based on value position in the sorted array
      const getColorIndex = (value) => {
        const position = sortedData.findIndex((item) => item.value === value);
        // Distribute into 9 buckets (based on colorScale array length)
        if (dataLength <= 1) return 4; // Middle color for single item

        // Calculate position percentage and map to color index (0-8)
        const percentage = position / (dataLength - 1);
        return Math.min(8, Math.floor(percentage * 9));
      };

      return data
        .map((item) => {
          if (!item || !item.country) return null;

          const countryCode = String(item.country).trim().toUpperCase();
          const countryName =
            item.name ||
            countriesMap[countryCode] ||
            safeGetName(countryCode) ||
            (countryCode === "XX" ? "Unknown" : countryCode);

          if (countryCode === "XX" || countryCode === "PROXY") {
            return null;
          }

          const colorIndex = getColorIndex(item.value);

          return {
            ...item,
            country: countryCode,
            countryName,
            colorIndex,
          };
        })
        .filter(Boolean);
    }, [data]);

    // Если данных нет, показываем сообщение
    if (!enhancedData.length) {
      return (
        <Box sx={{ textAlign: "center", p: 2 }}>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
            No country data available
          </Typography>
        </Box>
      );
    }

    // Отладочная информация
    console.log("Enhanced map data (first 3):", enhancedData.slice(0, 3));

    // Рендерим WorldMap с нашими улучшениями
    return (
      <WorldMap
        {...otherProps}
        data={enhancedData}
        tooltipTextFunction={(countryInfo, value) => {
          console.log("WorldMap tooltip called with:", {
            countryInfo,
            value,
            type: typeof countryInfo,
          });

          // Обработка случая, когда первый параметр - объект (что происходит сейчас)
          if (countryInfo && typeof countryInfo === "object") {
            // Извлекаем нужные данные прямо из объекта
            if (countryInfo.countryName) {
              return `${countryInfo.countryName}: ${countryInfo.countryValue || value} ${selectedMetric !== "campaigns" ? "users" : "campaigns"}`;
            } else if (countryInfo.countryCode) {
              // Если нет имени, но есть код
              const code = countryInfo.countryCode;
              const countryName =
                countriesMap[code] || safeGetName(code) || code;
              return `${countryName}: ${countryInfo.countryValue || value} ${selectedMetric !== "campaigns" ? "users" : "campaigns"}`;
            }
          }

          // Если это простая строка с кодом страны
          if (typeof countryInfo === "string") {
            const code = countryInfo.toUpperCase();
            const countryName = countriesMap[code] || safeGetName(code) || code;
            return `${countryName}: ${value} ${selectedMetric !== "campaigns" ? "users" : "campaigns"}`;
          }

          // Последний запасной вариант
          return `Unknown: ${value} ${selectedMetric !== "campaigns" ? "users" : "campaigns"}`;
        }}
        styleFunction={(context) => {
          // Get the colorScale from our options
          const { colorScale } = getColorScale();

          // Find the actual country data including colorIndex
          const countryData = enhancedData.find(
            (item) =>
              item.country === (context.countryCode || "").toUpperCase(),
          );

          // Use the specific color for this country based on its value
          let fillColor = "#f5f5f5"; // Default color for countries with no data

          if (countryData && countryData.value > 0) {
            // Use the colorIndex we calculated earlier
            const colorIndex = countryData.colorIndex || 4; // Default to middle color if missing
            fillColor = colorScale[colorIndex];
          }

          return {
            fill: fillColor,
            fillOpacity: 1,
            stroke: "#666",
            strokeWidth: 0.7,
            strokeOpacity: 0.8,
            cursor: "pointer",
          };
        }}
        color={acme.colors.foreground}
        size="lg"
        backgroundColor="#fff"
        strokeOpacity={0.8}
        borderColor="#666"
        tooltipBgColor="#333"
        tooltipTextColor="#fff"
        valuePrefix=""
        valueSuffix={selectedMetric !== "campaigns" ? " users" : "campaigns"}
        containerStyle={{
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      />
    );
  };

  // Helper functions for calculating change and rate percentages
  const calculateChange = (current, previous) => {
    if (previous === 0) return current > 0 ? current : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const calculateRate = (value, total) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  // Helper function to check if user is restricted
  const isUserRestricted = () => {
    return user?.status === "Moderation" || user?.status === "Block";
  };

  const formatSmsCredits = (value) =>
    Math.max(0, Math.floor(Number(value) || 0)).toLocaleString();

  const formatSmsEur = (value) => {
    const amount = Number(value) || 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `€${amount.toFixed(2)}`;
    }
  };

  const infoPaperSx = acmeInfoCardSx;

  const isPlanActive = Boolean(user.plan && user.plan !== "Basic");
  const isSmsActive =
    Boolean(user.unlimitedSms) || Number(user.smsCredits) > 0;

  const infoTitleSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    mb: 0.25,
  };

  const infoHeadingSx = {
    fontWeight: 600,
    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
    lineHeight: 1.3,
    color: acme.colors.foreground,
  };

  const infoPrimarySx = {
    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
    lineHeight: 1.4,
    color: acme.colors.foreground,
  };

  const infoSecondarySx = {
    fontSize: { xs: "0.6875rem", sm: "0.75rem" },
    lineHeight: 1.35,
    color: acme.colors.mutedForegroundSolid,
    mt: 0.25,
  };

  const infoIconSx = {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  };

  const dateDaySx = {
    fontWeight: 600,
    fontSize: { xs: "1.25rem", sm: "1.5rem" },
    lineHeight: 1.1,
  };

  const dateMetaSx = {
    display: "block",
    color: acme.colors.mutedForegroundSolid,
    fontSize: { xs: "0.65rem", sm: "0.7rem" },
    lineHeight: 1.3,
  };

  return (
    <PageShell>
      <PageSnackbars {...snackbarProps} />
      <Box sx={acmeDashboardPanelSx}>
        <Box
          sx={{
            mb: { xs: 1.25, sm: 1.5 },
            textAlign: "center",
            ...acmePageHeaderContentSx,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              mb: 0.25,
              color: acme.colors.foreground,
              letterSpacing: "-0.025em",
            }}
          >
            Hi, {user.login}! Welcome back!
          </Typography>
          <Typography sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" }, color: acme.colors.mutedForegroundSolid }}>
            Your monitoring dashboard
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: { xs: 1.25, sm: 1.5 },
            mb: 1.5,
            width: "100%",
          }}
        >
          {/* Account Status Warning */}
          {isUserRestricted() && (
            <Card
              sx={{
                ...infoPaperSx,
                gridColumn: { xs: "1 / -1", sm: "1 / -1" },
                bgcolor: user?.status === "Block" ? "#fef2f2" : "#fff7ed",
                borderColor: user?.status === "Block" ? "#fecaca" : "#fed7aa",
              }}
            >
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Box sx={infoTitleSx}>
                <WarningIcon
                  sx={{
                    color: user?.status === "Block" ? "#d32f2f" : "#f57c00",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    ...infoHeadingSx,
                    color: user?.status === "Block" ? "#d32f2f" : "#f57c00",
                  }}
                >
                  {user?.status === "Block"
                    ? "Account Blocked"
                    : "Account Under Moderation"}
                </Typography>
              </Box>
              <Typography sx={infoPrimarySx}>
                {user?.status === "Block"
                  ? "Your account has been blocked. All services are restricted. Please contact support for assistance."
                  : "Your account is under moderation. Some features may be limited. Please wait for moderation to complete or contact support."}
              </Typography>
              </CardContent>
            </Card>
          )}

          <Card sx={isPlanActive ? acmeInfoCardActiveSx("plan") : infoPaperSx}>
            {isPlanActive && (
              <Chip label="Active" size="small" sx={acmeActiveBadgeSx} />
            )}
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
            <Box sx={infoTitleSx}>
              <Box sx={infoIconSx}>
                <DashboardMetricIcon
                  name="plan"
                  size={18}
                  tone={isPlanActive ? "success" : "default"}
                />
              </Box>
              <Typography sx={infoHeadingSx}>Plan: {user.plan}</Typography>
            </Box>
            <Typography sx={infoPrimarySx}>
              Emails used: {user.emailsUsed} /{" "}
              {user.plan === "Unlimited" ? "Unlimited" : user.emailLimit}
            </Typography>
            <Typography sx={infoSecondarySx}>
              {user.plan === "Basic" ? (
                <>
                  Free plan · Next upgrade{" "}
                  {new Date(user.basicPlanRenewalDate).toLocaleDateString()}
                </>
              ) : user.plan === "Unlimited" ? (
                <>Unlimited plan</>
              ) : (
                <>
                  Purchased {new Date(user.purchasedDate).toLocaleDateString()} ·
                  Next payment{" "}
                  {new Date(user.nextPaymentDate).toLocaleDateString()}
                </>
              )}
            </Typography>
            </CardContent>
          </Card>

          <Card sx={isSmsActive ? acmeInfoCardActiveSx("sms") : infoPaperSx}>
            {isSmsActive && (
              <Chip label="Active" size="small" sx={acmeActiveBadgeSx} />
            )}
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
            <Box sx={infoTitleSx}>
              <Box sx={infoIconSx}>
                <DashboardMetricIcon
                  name="sms"
                  size={18}
                  tone={isSmsActive ? "success" : "default"}
                />
              </Box>
              <Typography sx={infoHeadingSx}>SMS Marketing</Typography>
            </Box>
            <Typography sx={infoPrimarySx}>
              SMS sent (all time): {formatSmsCredits(user.allSentSms || 0)}
              {user.unlimitedSms
                ? " · Unlimited"
                : ` · Credits: ${formatSmsCredits(user.smsCredits)} / ${formatSmsCredits(user.smsCreditsPeak)}`}
            </Typography>
            <Typography sx={infoSecondarySx}>
              {user.unlimitedSms
                ? "Unlimited SMS sending included."
                : user.smsCredits <= 0
                  ? "No credits · Top up in Billing"
                  : `${formatSmsEur(user.smsCreditsEur)} wallet · Top up in Billing`}
            </Typography>
            </CardContent>
          </Card>

          <Card
            sx={{
              ...infoPaperSx,
              position: "relative",
              gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "auto" },
            }}
          >
            <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    flex: 1,
                    minWidth: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => setOpenStartDatePicker(true)}
                >
                  <Typography sx={dateDaySx}>
                    {format(dateRange.startDate, "dd")}
                  </Typography>
                  <Typography sx={dateMetaSx}>
                    {format(dateRange.startDate, "MMM yyyy")}
                  </Typography>
                  <Typography
                    sx={{
                      ...dateMetaSx,
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {format(dateRange.startDate, "EEEE")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", px: 0.5 }}>
                  <CircleIcon sx={{ fontSize: "0.55rem", color: acme.colors.mutedForegroundSolid }} />
                </Box>
                <Box
                  sx={{
                    textAlign: "center",
                    flex: 1,
                    minWidth: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => setOpenEndDatePicker(true)}
                >
                  <Typography sx={dateDaySx}>
                    {format(dateRange.endDate, "dd")}
                  </Typography>
                  <Typography sx={dateMetaSx}>
                    {format(dateRange.endDate, "MMM yyyy")}
                  </Typography>
                  <Typography
                    sx={{
                      ...dateMetaSx,
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {format(dateRange.endDate, "EEEE")}
                  </Typography>
                </Box>
              </Box>

              {/* Hidden date pickers */}

              <Box
                sx={{
                  position: "absolute",
                  visibility: "hidden",
                  width: 0,
                  height: 0,
                }}
              >
                <DatePicker
                  label="From"
                  value={dateRange.startDate}
                  onChange={(newValue) =>
                    handleDateRangeChange("startDate", newValue)
                  }
                  open={openStartDatePicker}
                  onClose={() => setOpenStartDatePicker(false)}
                  slotProps={{
                    textField: {
                      size: "small",
                      inputProps: { style: { position: "absolute" } },
                      sx: { position: "absolute" },
                    },
                    popper: {
                      sx: {
                        zIndex: 1200,
                        "& .MuiPaper-root": {
                          border: `1px solid ${acme.colors.border}`,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateRange.endDate}
                  onChange={(newValue) =>
                    handleDateRangeChange("endDate", newValue)
                  }
                  open={openEndDatePicker}
                  onClose={() => setOpenEndDatePicker(false)}
                  slotProps={{
                    textField: {
                      size: "small",
                      inputProps: { style: { position: "absolute" } },
                      sx: { position: "absolute" },
                    },
                    popper: {
                      sx: {
                        zIndex: 1200,
                        "& .MuiPaper-root": {
                          border: `1px solid ${acme.colors.border}`,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        },
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Tabs
          value={value}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Emails" />
          {FEATURES.PROJECTS_ENABLED && <Tab label="Projects" />}
          <Tab label="SMS" />
          <Tab label="Notifications" />
          {!isEndUserRole(user.role) && <Tab label="Traffics" />}
        </Tabs>
      </Box>

      {/* Body Block - Statistics or Onboarding */}
      <Box sx={acmeDashboardTabGapSx}>
        {activeTab === "emails" && (
          <Box>
            {/* Show Onboarding for new users, otherwise show statistics */}
            {!onboardingLoading &&
            onboardingData.isNewUser &&
            !isOnboardingComplete ? (
              <OnboardingProgress
                onboardingData={onboardingData}
                refreshOnboardingData={refreshOnboardingData}
              />
            ) : (
              <>
                {/* Email Dashboard Content */}
                <Grid container spacing={DASHBOARD_STAT_GRID_SPACING} sx={acmeDashboardStatGridSx}>
                  {/* Campaigns */}
                  <Grid item xs={6} sm={6} md={2.4}>
                    {renderStatBox(
                      "CAMPAIGNS",
                      "campaigns",
                      emailStats.campaigns.count,
                      null,
                      null,
                      emailStats.campaigns.loading,
                    )}
                  </Grid>

                  {/* Recipients */}
                  <Grid item xs={6} sm={6} md={2.4}>
                    {renderStatBox(
                      "RECIPIENTS",
                      "recipients",
                      emailStats.recipients.count,
                      emailStats.recipients.rate,
                      emailStats.recipients.change,
                      emailStats.recipients.loading,
                    )}
                  </Grid>

                  {/* Opens */}
                  <Grid item xs={6} sm={6} md={2.4}>
                    {renderStatBox(
                      "OPENS",
                      "opens",
                      emailStats.opens.count,
                      calculateRate(
                        emailStats.opens.count,
                        emailStats.recipients.count,
                      ),
                      null,
                      emailStats.opens.loading,
                    )}
                  </Grid>

                  {/* Clicks */}
                  <Grid item xs={6} sm={6} md={2.4}>
                    {renderStatBox(
                      "CLICKS",
                      "clicks",
                      emailStats.clicks.count,
                      calculateRate(
                        emailStats.clicks.count,
                        emailStats.recipients.count,
                      ),
                      null,
                      emailStats.clicks.loading,
                    )}
                  </Grid>

                  {/* Unsubscribed */}
                  <Grid item xs={12} sm={6} md={2.4}>
                    {renderStatBox(
                      "UNSUBSCRIBED",
                      "unsubscribed",
                      emailStats.unsubscribed.count,
                      calculateRate(
                        emailStats.unsubscribed.count,
                        emailStats.recipients.count,
                      ),
                      null,
                      emailStats.unsubscribed.loading,
                    )}
                  </Grid>
                </Grid>

                {/* Country & Device Statistics */}
                <Grid container spacing={DASHBOARD_CHART_GRID_SPACING}>
                  {/* World Map - Country */}
                  <Grid item xs={12} md={8}>
                    <Card sx={{ ...acmeDashboardPanelCardSx, overflow: "hidden" }}>
                      <CardContent sx={acmeDashboardCardContentSx}>
                      <Typography variant="h6" sx={acmeDashboardSectionTitleSx}>
                        {getMetricName(selectedMetric)} by Country
                      </Typography>
                      <Typography sx={acmeDashboardSectionDescSx}>
                        Geographic distribution of{" "}
                        {getMetricName(selectedMetric).toLowerCase()} based on
                        selected date range
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          minHeight: { xs: "auto", sm: "400px" },
                          height:
                            showAllCountries && countryData.length > 6
                              ? "auto"
                              : { xs: "auto", sm: "400px" },
                          flexDirection: { xs: "column", sm: "row" },
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            flex: { xs: "1 1 auto", sm: "0 0 35%" },
                            pr: { xs: 0, sm: 2 },
                            maxHeight:
                              showAllCountries && countryData.length > 6
                                ? "none"
                                : { xs: "none", sm: "400px" },
                            overflowY:
                              showAllCountries && countryData.length > 6
                                ? "visible"
                                : { xs: "visible", sm: "auto" },
                            mb: { xs: 3, sm: 0 },
                            minWidth: 0,
                          }}
                        >
                          {countryDataLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          ) : (
                            <>
                              {countryData.length > 0 && (
                                <>
                                  {(showAllCountries
                                    ? countryData
                                    : countryData.slice(0, 6)
                                  ).map((item, index) => (
                                    <Box key={index} sx={{ mb: 2 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 0.5,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            maxWidth: "65%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {item.name ||
                                            countriesMap[item.country] ||
                                            (item.country === "XX"
                                              ? "Unknown"
                                              : item.country)}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                        >
                                          {item.value}
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          width: "100%",
                                          height: 8,
                                          bgcolor: acme.colors.muted,
                                          borderRadius: 1,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: `${Math.min(100, (item.value / (Math.max(...countryData.map((d) => d.value)) || 1)) * 100)}%`,
                                            height: "100%",
                                            bgcolor:
                                              getColorScale().colorScale[
                                                8 - Math.min(8, index)
                                              ],
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  ))}
                                  {countryData.length > 6 && (
                                    <Box sx={{ mt: 3, textAlign: "center" }}>
                                      <Button
                                        variant="link"
                                        onClick={() =>
                                          setShowAllCountries(!showAllCountries)
                                        }
                                      >
                                        {showAllCountries
                                          ? "Show less"
                                          : `Show all (${countryData.length})`}
                                      </Button>
                                    </Box>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Box>
                        <Box
                          sx={{
                            flex: { xs: "1 1 auto", sm: "0 0 65%" },
                            height: { xs: "300px", sm: "100%" },
                            overflow: "hidden",
                            minHeight: { xs: "300px", sm: "400px" },
                            minWidth: 0,
                          }}
                        >
                          {countryDataLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                width: "100%",
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                width: "100%",
                              }}
                            >
                              {/* Добавляем лог перед картой */}
                              {(() => {
                                const mapData = countryData
                                  .filter(
                                    (item) =>
                                      item &&
                                      item.country &&
                                      typeof item.value === "number",
                                  )
                                  .map((item) => ({
                                    country: item.country.toUpperCase(),
                                    value: item.value,
                                  }));

                                console.log(
                                  "Full country data before map:",
                                  mapData,
                                );
                                // Проверяем, есть ли в данных нужные поля
                                console.log(
                                  "Sample country record:",
                                  mapData.length > 0 ? mapData[0] : "No data",
                                );

                                // Проверяем наличие названий стран
                                if (mapData.length > 0) {
                                  const sampleCode = mapData[0].country;
                                  console.log(
                                    `Country name check for ${sampleCode}:`,
                                    countriesMap[sampleCode] ||
                                      safeGetName(sampleCode) ||
                                      "Not found",
                                  );
                                }

                                return null;
                              })()}

                              {/* Показываем обработанную карту */}
                              <EnhancedWorldMap
                                data={countryData.filter(
                                  (item) =>
                                    item &&
                                    item.country &&
                                    typeof item.value === "number",
                                )}
                                colorScaleOptions={getColorScale()}
                                valueSuffix={
                                  selectedMetric !== "campaigns"
                                    ? " users"
                                    : " campaigns"
                                }
                              />

                              {/* Add color legend for the map */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 10,
                                  right: 10,
                                  display: "flex",
                                  flexDirection: "column",
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  padding: 1,
                                  borderRadius: 1,
                                  boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                                  maxWidth: "150px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: "bold", mb: 0.5 }}
                                >
                                  {getMetricName(selectedMetric)}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor:
                                        getColorScale().colorScale[1],
                                      mr: 1,
                                      borderRadius: "2px",
                                    }}
                                  />
                                  <Typography variant="caption">Low</Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor:
                                        getColorScale().colorScale[4],
                                      mr: 1,
                                      borderRadius: "2px",
                                    }}
                                  />
                                  <Typography variant="caption">
                                    Medium
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor:
                                        getColorScale().colorScale[8],
                                      mr: 1,
                                      borderRadius: "2px",
                                    }}
                                  />
                                  <Typography variant="caption">
                                    High
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Mobile vs Desktop */}
                  <Grid item xs={12} md={4}>
                    <Card sx={acmeDashboardPanelCardSx}>
                      <CardContent sx={acmeDashboardCardContentSx}>
                      <Typography variant="h6" sx={acmeDashboardSectionTitleSx}>
                        {getMetricName(selectedMetric)} by Device
                      </Typography>
                      <Typography sx={acmeDashboardSectionDescSx}>
                        Distribution of{" "}
                        {getMetricName(selectedMetric).toLowerCase()} between
                        mobile and desktop devices
                      </Typography>

                      {deviceDataLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "60%",
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              my: { xs: 2, sm: 4 },
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: { xs: 150, sm: 200 },
                                height: { xs: 150, sm: 200 },
                              }}
                            >
                              {/* SVG circle with percentage */}
                              <svg
                                width="100%"
                                height="100%"
                                viewBox="0 0 200 200"
                              >
                                <circle
                                  cx="100"
                                  cy="100"
                                  r="90"
                                  fill="none"
                                  stroke={acme.colors.muted}
                                  strokeWidth="20"
                                />
                                <circle
                                  cx="100"
                                  cy="100"
                                  r="90"
                                  fill="none"
                                  stroke={acme.colors.foreground}
                                  strokeWidth="20"
                                  strokeDasharray={`${calculateMobilePercentage() * 5.65} 565`}
                                  strokeDashoffset="0"
                                  transform="rotate(-90 100 100)"
                                />
                              </svg>
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  textAlign: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: { xs: "1.75rem", sm: "2.5rem" },
                                    fontWeight: "bold",
                                    color: acme.colors.foreground,
                                  }}
                                >
                                  {calculateMobilePercentage()}%
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-around",
                              mt: { xs: 2, sm: 4 },
                            }}
                          >
                            <Box sx={{ textAlign: "center" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: acme.colors.foreground,
                                    mr: 1,
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  }}
                                >
                                  MOBILE
                                </Typography>
                              </Box>
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                }}
                              >
                                {deviceStats.mobile}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: acme.colors.border,
                                    mr: 1,
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                  }}
                                >
                                  DESKTOP
                                </Typography>
                              </Box>
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                }}
                              >
                                {deviceStats.desktop}
                              </Typography>
                            </Box>
                          </Box>
                        </>
                      )}
                    </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        )}

        {activeTab === "projects" && FEATURES.PROJECTS_ENABLED && (
          <DashboardProject
            dateRange={dateRange}
            calculateChange={calculateChange}
            calculateRate={calculateRate}
          />
        )}

        {activeTab === "sms" && <DashboardSMS dateRange={dateRange} />}

        {activeTab === "notifications" && (
          <Card sx={acmeDashboardPanelCardSx}>
            <CardContent sx={acmeDashboardCardContentSx}>
              <Notifications inDashboard={true} />
            </CardContent>
          </Card>
        )}

        {activeTab === "traffics" && (
          <DashboardTraffics
            dateRange={dateRange}
            calculateChange={calculateChange}
            calculateRate={calculateRate}
          />
        )}
      </Box>

      <WelcomeDialog
        open={showWelcome}
        onClose={handleWelcomeClose}
        onStart={handleWelcomeStart}
        user={user}
      />
    </PageShell>
  );
};

export default Dashboard;
